import { useEffect, useMemo, useState } from 'react';

import {
  CreateUpdateEstimateItemDto,
  GetPositionExtendedDto,
  GetProfileDto,
  GetProjectDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { usePokCore } from '../../../common/hooks/usePokCore';

import { fieldsToClear as getFieldsToClear } from './CommissionService';
import {
  getDefaultDeliveryTeam,
  getPathPhaseIdForSpecialistTeam,
  getPositionSpecialistTeamOptions,
  hasPositionSpecialistTeams,
} from './SpecialistTeamPhaseUtils';

const getTeamFieldsForPosition = (
  estimateItem: CreateUpdateEstimateItemDto,
  position: GetPositionExtendedDto,
  project: GetProjectDto | undefined,
): Pick<CreateUpdateEstimateItemDto, 'specialistTeamId' | 'deliveryTeamId'> => {
  if (hasPositionSpecialistTeams(position.positionSpecialistTeams)) {
    const options = getPositionSpecialistTeamOptions(
      position.positionSpecialistTeams,
      estimateItem.pathPhaseDictionaryId,
    );
    return {
      specialistTeamId:
        estimateItem.specialistTeamId &&
        options.allowedTeamIds.includes(estimateItem.specialistTeamId)
          ? estimateItem.specialistTeamId
          : null,
      deliveryTeamId: null,
    };
  }
  const previousDeliveryTeamId = estimateItem.specialistTeamId
    ? null
    : estimateItem.deliveryTeamId;
  return {
    specialistTeamId: null,
    deliveryTeamId:
      previousDeliveryTeamId ?? getDefaultDeliveryTeam(project)?.id ?? null,
  };
};

interface UseEstimateItemStateParams {
  estimateItem: CreateUpdateEstimateItemDto;
  id?: string;
  project?: GetProjectDto;
  profile?: GetProfileDto;
  onCompleted: (
    item: CreateUpdateEstimateItemDto,
    id?: string,
    position?: GetPositionExtendedDto,
  ) => Promise<{
    saved: boolean;
    errors: string[];
    amountFieldsValid?: boolean;
  }>;
  onDelete: (id?: string) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
  closeAccordion: () => void;
}

export const useEstimateItemState = (params: UseEstimateItemStateParams) => {
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('amounts');
  const [estimateItem, setEstimateItem] = useState<CreateUpdateEstimateItemDto>(
    params.estimateItem,
  );
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<
    GetPositionExtendedDto | undefined
  >(undefined);
  const pok = usePokCore();
  const notifications = useNotifications();

  useEffect(() => {
    let mounted = true;
    if (params.estimateItem.positionId) {
      pok.positions
        .getById(params.estimateItem.positionId)
        .then(position => {
          if (mounted) {
            setSelectedPosition(position);
          }
        })
        .catch(errorResponse => {
          notifications.caughtError(errorResponse as ResponseError);
        });
    }
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const specialistTeamOptions = useMemo(
    () =>
      getPositionSpecialistTeamOptions(
        selectedPosition?.positionSpecialistTeams,
        estimateItem?.pathPhaseDictionaryId,
      ),
    [selectedPosition, estimateItem?.pathPhaseDictionaryId],
  );

  const propertyChange = async (obj: Partial<CreateUpdateEstimateItemDto>) => {
    if (obj.positionId) {
      const oldPositionId = estimateItem.positionId;
      try {
        const oldPosition = oldPositionId
          ? await pok.positions.getById(oldPositionId)
          : undefined;
        const position = await pok.positions.getById(obj.positionId);
        const fieldsToClear = getFieldsToClear(
          position.positionType,
          oldPosition?.positionType,
          params.profile,
        );
        setEstimateItem({
          ...estimateItem,
          ...obj,
          ...fieldsToClear,
          ...getTeamFieldsForPosition(estimateItem, position, params.project),
          positionType: position.positionType,
        });
        setSelectedPosition(position);
      } catch (errorResponse) {
        notifications.caughtError(errorResponse as ResponseError);
      }
    } else {
      setEstimateItem({ ...estimateItem, ...obj });
    }
  };

  useEffect(() => {
    if (!selectedPosition) {
      return;
    }
    if (!hasPositionSpecialistTeams(selectedPosition.positionSpecialistTeams)) {
      const defaultDeliveryTeamId = getDefaultDeliveryTeam(params.project)?.id;
      if (!estimateItem.deliveryTeamId && defaultDeliveryTeamId) {
        void propertyChange({ deliveryTeamId: defaultDeliveryTeamId });
      }
      return;
    }
    if (!estimateItem.specialistTeamId && specialistTeamOptions.defaultTeamId) {
      const teamId = specialistTeamOptions.defaultTeamId;
      const pathPhaseDictionaryId = getPathPhaseIdForSpecialistTeam(
        selectedPosition.positionSpecialistTeams,
        teamId,
        params.project,
      );
      void propertyChange({
        specialistTeamId: teamId,
        ...(pathPhaseDictionaryId ? { pathPhaseDictionaryId } : {}),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialistTeamOptions, selectedPosition]);

  const createEstimateItem = async () => {
    setIsLoadingSave(true);
    params
      .onCompleted(estimateItem, params.id, selectedPosition)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          if (saveStatus.amountFieldsValid === true) {
            setActiveTab('parameters');
          }
          return;
        }
        setSaveErrors([]);
        setShowSaveErrors(false);
        notifications.saveCompleted();
        params.closeAccordion();
      })
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => setIsLoadingSave(false));
  };

  const deleteItem = async () => {
    setIsLoadingDelete(true);
    params
      .onDelete(params.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
        notifications.saveCompleted();
      })
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => setIsLoadingDelete(false));
  };

  return {
    estimateItem,
    selectedPosition,
    specialistTeamOptions,
    activeTab,
    setActiveTab,
    showSaveErrors,
    saveErrors,
    isLoadingSave,
    isLoadingDelete,
    propertyChange,
    createEstimateItem,
    deleteItem,
  };
};
